import { createClient, navigatorLock } from "@supabase/supabase-js";

/**
 * Auth wraps every session read/refresh in a cross-tab Web Lock. A frozen
 * background tab (or a refresh that never settled) can keep that lock forever,
 * so getSession() times out after 10s and uploads/requests fail with
 * "Acquiring an exclusive Navigator LockManager lock … timed out".
 * When acquiring times out, steal the lock instead of failing.
 */
const authLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  const locks = globalThis.navigator?.locks;
  if (!locks) return fn();

  let started = false;
  try {
    return await navigatorLock(name, acquireTimeout, () => {
      started = true;
      return fn();
    });
  } catch (error) {
    // 0 = "only if free" (background auto-refresh): keep failing fast.
    if (started || acquireTimeout <= 0) throw error;
    return locks.request(name, { mode: "exclusive", steal: true }, () => fn());
  }
};

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  { auth: { lock: authLock } },
);
